import type { MyFacilityListRow } from "types/myFacilityListRow";

export type PostPurchaseFacilityTarget = {
  facilityId?: number;
  facilityName?: string;
  facilityCity?: string;
  facilityTypeSlug?: string;
};

export const POST_PURCHASE_FACILITY_MAX_ATTEMPTS = 12;
export const POST_PURCHASE_FACILITY_RETRY_MS = 2000;

/** Whether navigation/session indicates we are waiting for a newly purchased facility row. */
export function hasPostPurchaseFacilityTarget(
  target: PostPurchaseFacilityTarget | null | undefined,
): boolean {
  if (!target) return false;
  return (
    (typeof target.facilityId === "number" &&
      Number.isFinite(target.facilityId)) ||
    !!(target.facilityName && target.facilityName.trim())
  );
}

export type PostPurchaseNavigationHints = {
  facilityId?: number;
  facilityName?: string;
  facilityCity?: string;
  facilityTypeSlug?: string;
};

/** Merge router state + sessionStorage post-purchase keys into a single match target. */
export function buildPostPurchaseTargetFromNavigation(
  navigationState: PostPurchaseNavigationHints | null | undefined,
  sessionSelection: PostPurchaseNavigationHints | null | undefined,
): PostPurchaseFacilityTarget | null {
  const target: PostPurchaseFacilityTarget = {
    facilityId: navigationState?.facilityId ?? sessionSelection?.facilityId,
    facilityName:
      navigationState?.facilityName ?? sessionSelection?.facilityName,
    facilityCity: navigationState?.facilityCity ?? sessionSelection?.facilityCity,
    facilityTypeSlug:
      navigationState?.facilityTypeSlug ?? sessionSelection?.facilityTypeSlug,
  };
  return hasPostPurchaseFacilityTarget(target) ? target : null;
}

/** Do not restore the prior session facility while resolving a new purchase. */
export function shouldDeferSessionSelectedFacilityOnMount(
  target: PostPurchaseFacilityTarget | null | undefined,
): boolean {
  return hasPostPurchaseFacilityTarget(target);
}

/**
 * An empty facility list only means "user has none" once loading finished. While the
 * mount fetch is in flight the restored session facility must survive, or returning
 * to the dashboard (e.g. Survey Viewer back arrow) falls back to the first facility.
 */
export function shouldClearSelectionForEmptyFacilities(
  hasSelection: boolean,
  facilitiesLoading: boolean,
): boolean {
  return hasSelection && !facilitiesLoading;
}

export function findFacilityForPostPurchaseTarget<
  T extends Pick<
    MyFacilityListRow,
    "facility_id" | "facility_name" | "city"
  >,
>(facilities: T[], target: PostPurchaseFacilityTarget): T | undefined {
  const navFacilityId = target.facilityId;
  const navFacilityName = target.facilityName?.trim();
  const navFacilityCity = target.facilityCity?.trim();

  if (navFacilityId != null) {
    const byId = facilities.find((f) => f.facility_id === navFacilityId);
    if (byId) return byId;
  }

  if (navFacilityName) {
    return facilities.find(
      (f) =>
        f.facility_name?.toLowerCase() === navFacilityName.toLowerCase() &&
        (!navFacilityCity ||
          f.city?.toLowerCase() === navFacilityCity.toLowerCase()),
    );
  }

  return undefined;
}

export function shouldRetryPostPurchaseFacilityFetch(
  resolved: boolean,
  attempt: number,
  maxAttempts: number = POST_PURCHASE_FACILITY_MAX_ATTEMPTS,
): boolean {
  return !resolved && attempt < maxAttempts;
}

export function postPurchaseFacilityMatchesTarget(
  facility: Pick<MyFacilityListRow, "facility_id" | "facility_name">,
  target: PostPurchaseFacilityTarget,
): boolean {
  if (
    typeof target.facilityId === "number" &&
    Number.isFinite(target.facilityId) &&
    facility.facility_id === target.facilityId
  ) {
    return true;
  }
  if (target.facilityName?.trim()) {
    return (
      facility.facility_name?.toLowerCase() ===
      target.facilityName.trim().toLowerCase()
    );
  }
  return false;
}
